import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AutomationStatus,
  ExperienceAnchorType,
  IExperience,
  IExperienceTemplate,
  MessageStatus,
  PARTICIPANT_ACTOR_KEY,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { compact, flatten, uniq } from 'lodash';
import { useCallback, useMemo } from 'react';
import {
  ItemSorter,
  RowsRenderer,
  ROW_HEIGHTS,
  Sorters
} from '../../../../components/GroupableList';
import { Loader } from '../../../../components/Loader';
import { SomethingWentWrongWithReport } from '../../../../components/SomethingWentWrong';
import { IColumn } from '../../../../components/Table/VirtualizedSortable';
import { useSort } from '../../../../hooks/useSort';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { useCurrentUser } from '../../../../services/currentUser';
import {
  getAllActorUserIds,
  isExperienceUserActor,
  useExperienceInstancesByIds
} from '../../../../services/experiences/instance';
import { getMostRecentSuccessfulConcreteMessageOrAnyOther } from '../../../../services/experiences/message';
import {
  getTaskTemplateById,
  useExperienceTemplateById
} from '../../../../services/experiences/template';
import {
  useCombineLoadingValues,
  useMappedLoadingValue
} from '../../../../services/firebase';
import {
  D,
  withActorOrNull,
  withParticipantOrNull
} from '../../../../services/rowsRendererFunctions';
import { toMoment } from '../../../../services/time';
import { toFullName, useUserMapById } from '../../../../services/user';
import { AutomationStatusChip } from '../../components/AutomationStatusChip';
import { formatAutomationToExpandableComponent } from '../../components/ExecutionReportTable/Automations';
import { NotApplicable } from '../../components/ExecutionReportTable/components/NotApplicable';
import { TimeZone } from '../../components/ExecutionReportTable/components/Timezone';
import {
  formatTaskToExpandableComponent,
  Participant
} from '../../components/ExecutionReportTable/Tasks';
import { MessageStatusChip } from '../../components/MessageStatusChip';
import { AnchorDateDiffWarning } from '../../components/ParticipantsTable';
import { TaskStatusChip } from '../../components/TaskStatusChip';
import { CalV2Item } from '../../pages/Details/CalendarV2/types';
import { formatMessageToExpandableComponent } from '../ExecutionReportTable/Messages';

type ColumnName =
  | 'supportingActorName'
  | 'participant'
  | 'firstDayAtWork'
  | 'sentOn'
  | 'startedOn'
  | 'runDate';

type OtherProps = {
  tpl: Doc<IExperienceTemplate>;
  itemTplId: string;
  actor: { key: string; label: string };
  onShowInstanceDetails: (d: Doc<IExperience>) => void;
};

type Column = IColumn<D, ColumnName, OtherProps>;

const COMMON_COLUMNS: Column[] = [
  {
    key: 'firstDayAtWork',
    head: () => 'First day at work',
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        const currentUser = useCurrentUser();
        const { org } = currentUser;
        if (
          o.tpl.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE &&
          o.tpl.data.anchor.field === 'hireDate'
        ) {
          const originalDate =
            u.data.hireDate && toMoment(u.data.hireDate).tz(o.tpl.data.tz);
          // This is a rare exception for broken instances which could NOT be migrated when this field was introduced
          if (!d.exp.data.anchorDate?.at) {
            return (
              <FlexContainer>
                <div>{originalDate?.format(org.dateFormats.MMDDYYYY)}</div>
              </FlexContainer>
            );
          }
          const currentDate = toMoment(d.exp.data.anchorDate.at).tz(
            o.tpl.data.tz
          );
          return (
            <FlexContainer>
              <div>{currentDate.format(org.dateFormats.MMDDYYYY)}</div>
              <AnchorDateDiffWarning
                original={originalDate}
                current={currentDate}
              />
            </FlexContainer>
          );
        }
        return u.data.hireDate
          ? toMoment(u.data.hireDate).format(org.dateFormats.MMDDYYYY)
          : '';
      }),
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  }
];

const COMMON_MESSAGE_COLUMNS: Column[] = [
  ...COMMON_COLUMNS,
  {
    key: 'sentOn',
    head: () => 'Sent on',
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        const { t } = useUnsafeTranslation();
        const currentUser = useCurrentUser();
        const { org } = currentUser;
        const message =
          o.itemTplId &&
          getMostRecentSuccessfulConcreteMessageOrAnyOther(
            o.itemTplId,
            d.exp.data.messages,
            d.exp.data.messagesStatuses
          );
        if (
          (o.itemTplId &&
            d.exp.data.messagesStatuses[o.itemTplId].status ===
              MessageStatus.UPCOMING) ||
          !message
        ) {
          return <NotApplicable>{t('N/A')}</NotApplicable>;
        }
        //Show error sent date but in lighter gray
        if (
          message.status === MessageStatus.ERROR ||
          message.status === 'BOUNCED'
        ) {
          return (
            <NotApplicable>
              <FlexContainer>
                {toMoment(message.at)
                  .tz(o.tpl.data.tz)
                  .format(`${org.dateFormats.MMDDYYYY} HH:mm`)}{' '}
                {toMoment(message.at).tz(o.tpl.data.tz).format('z')}
              </FlexContainer>
            </NotApplicable>
          );
        }
        return (
          <FlexContainer>
            {toMoment(message.at)
              .tz(o.tpl.data.tz)
              .format(`${org.dateFormats.MMDDYYYY} HH:mm`)}
            <TimeZone>
              {' '}
              {toMoment(message.at).tz(o.tpl.data.tz).format('z')}
            </TimeZone>
          </FlexContainer>
        );
      }),
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  }
];

const MESSAGES_SUPPORTING_ACTOR_COLUMNS: Column[] = [
  {
    key: 'supportingActorName',
    head: (o) => o?.actor.label,
    cell: (d, o) => {
      return withActorOrNull(d, o.actor.key, (u) => {
        const message =
          o.itemTplId &&
          getMostRecentSuccessfulConcreteMessageOrAnyOther(
            o.itemTplId,
            d.exp.data.messages,
            d.exp.data.messagesStatuses
          );
        return (
          <FlexContainer>
            {d.exp.data.messagesStatuses[`${o.itemTplId}`].status ===
            MessageStatus.UPCOMING ? (
              <MessageStatusChip
                autoWidth={true}
                status={MessageStatus.UPCOMING}
              />
            ) : (
              <>
                {message && (
                  <MessageStatusChip
                    autoWidth={true}
                    status={
                      message.status === MessageStatus.ERROR ||
                      message.status === 'BOUNCED'
                        ? MessageStatus.ERROR
                        : MessageStatus.DONE
                    }
                  />
                )}
              </>
            )}
            <Participant
              participant={
                toFullName(u.data) || u.data.email || u.data.personalEmail
              }
              onClick={() => o.onShowInstanceDetails(d.exp)}
            />
          </FlexContainer>
        );
      });
    },
    align: 'left',
    sortable: false,
    width: 150,
    flexGrow: 1
  },
  {
    key: 'participant',
    head: () => 'Participant',
    cell: (d, o) =>
      withParticipantOrNull(
        d,
        (u) => toFullName(u.data) || u.data.email || u.data.personalEmail
      ),
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  ...COMMON_MESSAGE_COLUMNS
];

const MESSAGES_PARTICIPANT_COLUMNS: Column[] = [
  {
    key: 'participant',
    head: () => 'Participant',
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        const message =
          o.itemTplId &&
          getMostRecentSuccessfulConcreteMessageOrAnyOther(
            o.itemTplId,
            d.exp.data.messages,
            d.exp.data.messagesStatuses
          );
        return (
          <FlexContainer>
            {d.exp.data.messagesStatuses[`${o.itemTplId}`].status ===
            MessageStatus.UPCOMING ? (
              <MessageStatusChip
                autoWidth={true}
                status={MessageStatus.UPCOMING}
              />
            ) : (
              <>
                {message && (
                  <MessageStatusChip
                    autoWidth={true}
                    status={
                      message.status === MessageStatus.ERROR ||
                      message.status === 'BOUNCED'
                        ? MessageStatus.ERROR
                        : MessageStatus.DONE
                    }
                  />
                )}
              </>
            )}
            <Participant
              participant={
                toFullName(u.data) || u.data.email || u.data.personalEmail
              }
              onClick={() => o.onShowInstanceDetails(d.exp)}
            />
          </FlexContainer>
        );
      }),
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  ...COMMON_MESSAGE_COLUMNS
];

const COMMON_TASKS_COLUMNS: Column[] = [
  ...COMMON_COLUMNS,
  {
    key: 'startedOn',
    head: () => 'Started on',

    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        const currentUser = useCurrentUser();
        const { org } = currentUser;
        const { t } = useUnsafeTranslation();
        const task = d.exp.data.tasks.find(
          (task) => task.templateId === o.itemTplId
        );
        if ((o.itemTplId && task?.status === TaskStatus.NOT_STARTED) || !task) {
          return <NotApplicable>{t('N/A')}</NotApplicable>;
        }
        return (
          task && (
            <FlexContainer>
              {toMoment(task.timing.start)
                .tz(o.tpl.data.tz)
                .format(`${org.dateFormats.MMDDYYYY} HH:mm`)}
              <TimeZone>
                {' '}
                {toMoment(task.timing.start).tz(o.tpl.data.tz).format('z')}
              </TimeZone>
            </FlexContainer>
          )
        );
      }),
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  }
];

const TASKS_SUPPORTING_ACTORS_COLUMNS: Column[] = [
  {
    key: 'supportingActorName',
    head: (o) => o?.actor.label,
    cell: (d, o) => {
      return withActorOrNull(d, o.actor.key, (u) => {
        const task = d.exp.data.tasks.find(
          (task) => task.templateId === o.itemTplId
        );
        return (
          <FlexContainer>
            {task && <TaskStatusChip autoWidth status={task.status} />}
            <Participant
              participant={
                toFullName(u.data) || u.data.email || u.data.personalEmail
              }
              onClick={() => o.onShowInstanceDetails(d.exp)}
            />
          </FlexContainer>
        );
      });
    },
    align: 'left',
    sortable: false,
    width: 150,
    flexGrow: 1
  },
  {
    key: 'participant',
    head: () => 'Participant',
    cell: (d, o) =>
      withParticipantOrNull(
        d,
        (u) => toFullName(u.data) || u.data.email || u.data.personalEmail
      ),
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  ...COMMON_TASKS_COLUMNS
];

const TASKS_PARTICIPANTS_COLUMNS: Column[] = [
  {
    key: 'participant',
    head: () => 'Participant',
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        const task = d.exp.data.tasks.find(
          (task) => task.templateId === o.itemTplId
        );
        return (
          <FlexContainer>
            {task && <TaskStatusChip autoWidth status={task.status} />}
            <Participant
              participant={
                toFullName(u.data) || u.data.email || u.data.personalEmail
              }
              onClick={() => o.onShowInstanceDetails(d.exp)}
            />
          </FlexContainer>
        );
      }),
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  ...COMMON_TASKS_COLUMNS
];

const AUTOMATION_COLUMNS: Column[] = [
  {
    key: 'participant',
    head: () => 'Participant',
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        return (
          <FlexContainer>
            <>
              {d.exp.data.automations !== null && (
                <AutomationStatusChip
                  autoWidth={true}
                  status={
                    d.exp.data.automations[`${o.itemTplId}`].status ===
                    'UPCOMING'
                      ? AutomationStatus.UPCOMING
                      : d.exp.data.automations[`${o.itemTplId}`].status ===
                        'ERROR'
                      ? AutomationStatus.ERROR
                      : d.exp.data.automations[`${o.itemTplId}`].status ===
                        'DONE'
                      ? AutomationStatus.DONE
                      : // TRIGGERED is pending, not a skip — was mislabeled "Skipped".
                      d.exp.data.automations[`${o.itemTplId}`].status ===
                        'TRIGGERED'
                      ? AutomationStatus.TRIGGERED
                      : AutomationStatus.SKIPPED
                  }
                />
              )}
            </>
            <Participant
              participant={
                toFullName(u.data) || u.data.email || u.data.personalEmail
              }
              onClick={() => o.onShowInstanceDetails(d.exp)}
            />
          </FlexContainer>
        );
      }),
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  ...COMMON_COLUMNS,
  {
    key: 'runDate',
    head: () => 'Run date',
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        const currentUser = useCurrentUser();
        const { org } = currentUser;
        const { t } = useUnsafeTranslation();

        if (
          (o.itemTplId &&
            d.exp.data.automations[o.itemTplId].status ===
              AutomationStatus.UPCOMING) ||
          !d.exp.data.automations[o.itemTplId]
        ) {
          return <NotApplicable>{t('N/A')}</NotApplicable>;
        }
        return (
          <FlexContainer>
            {o.itemTplId &&
              toMoment(d.exp.data.automations[o.itemTplId].timing.at)
                .tz(o.tpl.data.tz)
                .format(`${org.dateFormats.MMDDYYYY} HH:mm`)}
            <TimeZone>
              {' '}
              {o.itemTplId &&
                toMoment(d.exp.data.automations[o.itemTplId].timing.at)
                  .tz(o.tpl.data.tz)
                  .format('z')}
            </TimeZone>
          </FlexContainer>
        );
      }),
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  }
];

type GroupSummary = {
  count: number;
};

const SORTERS: Sorters<GroupSummary, D, OtherProps> = {
  firstDayAtWork: {
    key: 'firstDayAtWork',
    label: 'First day at work',
    groups: { sort: (g) => g.key, dir: 'asc' },
    items: {
      // Sort by the INSTANCE anchor date (the experience's "first day"), not the
      // user-profile hireDate; fall back to hireDate only for un-migrated
      // instances that have no anchorDate.at (mirrors ParticipantsTable).
      sort: (d) =>
        d.exp.data.anchorDate?.at?.toMillis() ??
        withParticipantOrNull(d, (u) => u.data.hireDate?.toMillis()) ??
        0,
      dir: 'asc'
    }
  },

  runDate: {
    key: 'runDate',
    label: 'Run date',
    groups: { sort: (g) => g.key, dir: 'asc' },
    items: {
      sort: (d, o) =>
        (o.itemTplId &&
          d.exp.data.automations[o.itemTplId].timing.at.toMillis()) ||
        0,
      dir: 'asc'
    }
  },

  sentOn: {
    key: 'sentOn',
    label: 'Sent On',
    groups: { sort: (g) => g.key, dir: 'asc' },
    items: {
      // Sort by ACTUAL send date (concrete record's `at`) when sent, else the
      // scheduled `timing.at` — matches the Schedule's actual-send bucketing.
      sort: (d, o) => {
        if (!o.itemTplId) return 0;
        const concrete = getMostRecentSuccessfulConcreteMessageOrAnyOther(
          o.itemTplId,
          d.exp.data.messages,
          d.exp.data.messagesStatuses
        );
        const actualSendAt = concrete && 'at' in concrete ? concrete.at : null;
        return (
          (
            actualSendAt ?? d.exp.data.messagesStatuses[o.itemTplId]?.timing.at
          )?.toMillis() || 0
        );
      },
      dir: 'asc'
    }
  },

  // TODO: fix sorting for task based on starting date
  startedOn: {
    key: 'startedOn',
    label: 'Started on',
    groups: { sort: (g) => g.key, dir: 'asc' },
    items: {
      sort: (d, o) =>
        d.exp.data.tasks
          .find((task) => task.templateId === o.itemTplId)
          ?.timing.end.toMillis() || 0,
      dir: 'asc'
    }
  }
};

const ROW_TO_KEY = (d: D) => d.exp.id;
const DEFAULT_SORTER = SORTERS.firstDayAtWork;
const LOADER_HEIGHT = 500;

type TimelineItemExecutionTableProps = {
  type: 'message' | 'automation' | 'task';
  instances: Doc<IExperience>[];
  itemTplId: string;
  tpl: Doc<IExperienceTemplate>;
  actor: { key: string; label: string };
  onShowInstanceDetails: (d: Doc<IExperience>) => void;
};

export const TimelineItemExecutionTable = ({
  type,
  instances,
  itemTplId,
  tpl,
  actor,
  onShowInstanceDetails
}: TimelineItemExecutionTableProps) => {
  const { sortBy, direction, setSort } = useSort<ItemSorter<D, OtherProps>>(
    DEFAULT_SORTER,
    DEFAULT_SORTER.items.dir
  );

  const { org } = useCurrentUser();

  const userIds = useMemo(
    () => uniq(flatten(instances.map((d) => getAllActorUserIds(d)))),
    [instances]
  );

  const toRows = useCallback(
    (userMap: { [key: string]: Doc<IUser> }) =>
      instances.map<D>((exp) => {
        const actors: { [actorKey: string]: Doc<IUser> } = {};
        exp.data.actors.filter(isExperienceUserActor).forEach((a) => {
          actors[a.key] = userMap[a.userId] || null;
        });
        return {
          exp,
          actors: actors
        };
      }),
    [instances]
  );

  const [rows, loading] = useMappedLoadingValue(
    useUserMapById(tpl.data.orgId, userIds),
    toRows,
    true
  );

  const otherProps: OtherProps = useMemo(
    () => ({
      tpl,
      itemTplId,
      actor,
      onShowInstanceDetails
    }),
    [tpl, itemTplId, actor, onShowInstanceDetails]
  );

  const dialogTableColumns = useMemo(() => {
    const isActorParticipant = actor.key === PARTICIPANT_ACTOR_KEY;
    if (type === 'task') {
      return isActorParticipant
        ? TASKS_PARTICIPANTS_COLUMNS
        : TASKS_SUPPORTING_ACTORS_COLUMNS;
    } else if (type === 'message') {
      return isActorParticipant
        ? MESSAGES_PARTICIPANT_COLUMNS
        : MESSAGES_SUPPORTING_ACTOR_COLUMNS;
    } else if (type === 'automation') {
      return AUTOMATION_COLUMNS;
    } else return [];
  }, [type, actor.key]);

  const expandableElements = useMemo(() => {
    if (!rows) return;
    if (type === 'automation') {
      return formatAutomationToExpandableComponent(rows, itemTplId, tpl, org);
    } else if (type === 'task') {
      const taskTpl = getTaskTemplateById(tpl, itemTplId);
      if (!taskTpl) {
        return;
      }
      return formatTaskToExpandableComponent(rows, taskTpl);
    } else if (type === 'message') {
      return formatMessageToExpandableComponent(rows, itemTplId, tpl);
    } else return;
  }, [type, itemTplId, tpl, rows]);

  if (loading) {
    return <Loader height={LOADER_HEIGHT} />;
  }

  if (!rows) {
    return null;
  }

  return (
    <RowsRenderer
      variant="contained"
      columns={dialogTableColumns}
      rows={rows}
      rowToKey={ROW_TO_KEY}
      sorter={sortBy}
      sortDirection={direction}
      renderHead={true}
      headProps={{ sticky: true }}
      onHeadClick={(c, dir) => {
        const sorter = SORTERS[c.key];
        if (!sorter) {
          return;
        }
        setSort(sorter, dir || sorter.items.dir);
      }}
      chunkSize={30}
      rootMargin="300px"
      rowHeight={ROW_HEIGHTS.dense}
      expandableElements={expandableElements}
      otherProps={otherProps}
      sidePadding
    />
  );
};

export const TimelineItemExecutionTableLazy = ({
  instanceIds,
  tplId,
  ...props
}: Omit<TimelineItemExecutionTableProps, 'instances' | 'tpl'> & {
  tplId: string;
  instanceIds: string[];
}) => {
  console.log('INSTANCE IDS:', instanceIds);
  const [v, loading] = useCombineLoadingValues(
    useExperienceTemplateById(tplId),
    useMappedLoadingValue(useExperienceInstancesByIds(instanceIds), compact)
  );

  if (loading || !v) {
    return <Loader height={LOADER_HEIGHT} />;
  }

  const [tpl, instances] = v;
  if (!tpl) {
    return (
      <SomethingWentWrongWithReport
        height={300}
        context={{ page: 'calendar_dialog_table', section: 'timeline_execution' }}
      />
    );
  }
  return (
    <TimelineItemExecutionTable {...props} instances={instances} tpl={tpl} />
  );
};

export const CalV2TimelineItemExecutionTable = ({
  renderedItem,
  instances,
  itemTplId,
  tpl,
  actor,
  onShowInstanceDetails
}: {
  renderedItem: CalV2Item;
  instances: Doc<IExperience>[];
  itemTplId: string;
  tpl: Doc<IExperienceTemplate>;
  actor: { key: string; label: string };
  onShowInstanceDetails: (d: Doc<IExperience>) => void;
}) => {
  const relevantInstances = useMemo(() => {
    return instances.filter((instance) => {
      return renderedItem.instanceIds.includes(instance.id);
    });
  }, [instances, renderedItem.instanceIds]);

  return (
    <TimelineItemExecutionTable
      type={renderedItem.type}
      instances={relevantInstances}
      itemTplId={itemTplId}
      tpl={tpl}
      actor={actor}
      onShowInstanceDetails={onShowInstanceDetails}
    />
  );
};
